import {
  isFacilityId,
  type FacilityId,
} from '../data/facilities'
import {
  isSpaceMissionId,
  type SpaceMissionId,
} from '../data/spaceMissions'
import {
  isTechnologyId,
  type TechnologyId,
} from '../data/technologies'
import {
  getPrototypeRegion,
  isActiveMajorPowerId,
  type ActiveMajorPowerId,
} from '../data/regions'

const MAX_STRATEGIC_EVENTS = 40

interface StrategicEventBase {
  id: number
  turn: number
  nationId: ActiveMajorPowerId
}

export type StrategicEvent =
  | (StrategicEventBase & {
      type: 'research-start' | 'research-complete'
      technologyId: TechnologyId
    })
  | (StrategicEventBase & {
      type: 'construction-start' | 'construction-complete'
      regionId: string
      facilityId: FacilityId
    })
  | (StrategicEventBase & {
      type: 'space-mission-start' | 'space-mission-complete'
      missionId: SpaceMissionId
    })
  | (StrategicEventBase & {
      type: 'victory'
      missionId: SpaceMissionId
    })

export type StrategicEventDraft =
  | {
      turn: number
      nationId: ActiveMajorPowerId
      type: 'research-start' | 'research-complete'
      technologyId: TechnologyId
    }
  | {
      turn: number
      nationId: ActiveMajorPowerId
      type: 'construction-start' | 'construction-complete'
      regionId: string
      facilityId: FacilityId
    }
  | {
      turn: number
      nationId: ActiveMajorPowerId
      type: 'space-mission-start' | 'space-mission-complete'
      missionId: SpaceMissionId
    }
  | {
      turn: number
      nationId: ActiveMajorPowerId
      type: 'victory'
      missionId: SpaceMissionId
    }

export interface StrategicLogState {
  nextEventId: number
  entries: StrategicEvent[]
}

export function createInitialStrategicLog(): StrategicLogState {
  return {
    nextEventId: 1,
    entries: [],
  }
}

export function appendStrategicEvents(
  log: StrategicLogState,
  drafts: StrategicEventDraft[],
): StrategicLogState {
  if (drafts.length === 0) {
    return log
  }

  let nextEventId = log.nextEventId
  const appended = drafts.map((draft) => {
    const event = {
      ...draft,
      id: nextEventId,
    } as StrategicEvent
    nextEventId += 1
    return event
  })

  return {
    nextEventId,
    entries: [...log.entries, ...appended].slice(-MAX_STRATEGIC_EVENTS),
  }
}

export function isStrategicLogState(
  value: unknown,
): value is StrategicLogState {
  if (
    !isRecord(value) ||
    typeof value.nextEventId !== 'number' ||
    !Number.isInteger(value.nextEventId) ||
    value.nextEventId < 1 ||
    !Array.isArray(value.entries) ||
    value.entries.length > MAX_STRATEGIC_EVENTS
  ) {
    return false
  }

  const ids = new Set<number>()
  let maxId = 0

  for (const event of value.entries) {
    if (!isStrategicEvent(event) || ids.has(event.id)) {
      return false
    }
    ids.add(event.id)
    maxId = Math.max(maxId, event.id)
  }

  return value.nextEventId > maxId
}

function isStrategicEvent(value: unknown): value is StrategicEvent {
  if (
    !isRecord(value) ||
    typeof value.id !== 'number' ||
    !Number.isInteger(value.id) ||
    value.id < 1 ||
    typeof value.turn !== 'number' ||
    !Number.isInteger(value.turn) ||
    value.turn < 1 ||
    !isActiveMajorPowerId(value.nationId)
  ) {
    return false
  }

  if (
    value.type === 'research-start' ||
    value.type === 'research-complete'
  ) {
    return isTechnologyId(value.technologyId)
  }

  if (
    value.type === 'construction-start' ||
    value.type === 'construction-complete'
  ) {
    return (
      typeof value.regionId === 'string' &&
      getPrototypeRegion(value.regionId) !== undefined &&
      isFacilityId(value.facilityId)
    )
  }

  if (
    value.type === 'space-mission-start' ||
    value.type === 'space-mission-complete' ||
    value.type === 'victory'
  ) {
    return isSpaceMissionId(value.missionId)
  }

  return false
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
